/**
 * EventBus — typed event emitter for decoupled communication between systems.
 *
 * Mirrors the pattern from the Phaser codebase (scene.events.emit / .on / .off)
 * but standalone, not tied to any engine.
 *
 * Now generic over an EventPayloadMap so that emit/on/once are type-checked.
 * Events NOT in the map still work via the fallback overload signatures,
 * allowing incremental migration.
 */

import type { EventPayloadMap } from "../config/events";

// Internal storage type — callbacks are untyped internally
type AnyCallback = (...args: any[]) => void;

const DEFAULT_EVENT_HISTORY_LIMIT = 250;
const MAX_EVENT_SNAPSHOT_DEPTH = 4;
const MAX_EVENT_SNAPSHOT_ITEMS = 20;

// Exact type equality check
type IsExactly<T, U> = [T] extends [U] ? ([U] extends [T] ? true : false) : false;

// Helper: resolve payload for mapped events; fall back for unmapped.
type Payload<K extends string> = K extends keyof EventPayloadMap
  ? EventPayloadMap[K]
  : unknown;

// Helper: callback type for a given payload
type TypedCallback<P> =
  IsExactly<P, void> extends true ? () => void :
  IsExactly<P, unknown> extends true ? (...args: any[]) => void :
  (data: P) => void;

// Helper: arguments for emit
type EmitArgs<P> =
  IsExactly<P, void> extends true ? [] :
  IsExactly<P, unknown> extends true ? any[] :
  [P];

export interface EventLogEntry {
  seq: number;
  event: string;
  timestampMs: number;
  isoTime: string;
  listenerCount: number;
  payload?: unknown;
}

function normalizeHistoryLimit(limit: number): number {
  if (!Number.isFinite(limit)) {
    return DEFAULT_EVENT_HISTORY_LIMIT;
  }
  return Math.max(0, Math.floor(limit));
}

function snapshotEventValue(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (typeof value === "bigint") {
    return `${value}n`;
  }

  if (typeof value === "function") {
    return "[Function]";
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
    };
  }

  if (depth >= MAX_EVENT_SNAPSHOT_DEPTH) {
    if (Array.isArray(value)) {
      return `[Array(${value.length})]`;
    }
    const ctor = (value as { constructor?: { name?: string } }).constructor?.name ?? "Object";
    return `[${ctor}]`;
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_EVENT_SNAPSHOT_ITEMS)
      .map((item) => snapshotEventValue(item, depth + 1, seen));
  }

  if (typeof value === "object") {
    if (seen.has(value)) {
      return "[Circular]";
    }
    seen.add(value);

    const out: Record<string, unknown> = {};
    const entries = Object.entries(value as Record<string, unknown>);
    for (const [index, [key, nestedValue]] of entries.entries()) {
      if (index >= MAX_EVENT_SNAPSHOT_ITEMS) {
        out.__truncated__ = `+${entries.length - index} keys`;
        break;
      }
      out[key] = snapshotEventValue(nestedValue, depth + 1, seen);
    }
    return out;
  }

  return String(value);
}

export class EventBus {
  private listeners: Map<string, Set<AnyCallback>> = new Map();
  private history: EventLogEntry[] = [];
  private historyLimit: number;
  private sequence = 0;

  constructor(options?: { historyLimit?: number }) {
    this.historyLimit = normalizeHistoryLimit(options?.historyLimit ?? DEFAULT_EVENT_HISTORY_LIMIT);
  }

  private recordEvent(event: string, args: unknown[], listenerCount: number): void {
    if (this.historyLimit === 0) {
      return;
    }

    const timestampMs = Date.now();
    const payload =
      args.length === 0
        ? undefined
        : args.length === 1
          ? snapshotEventValue(args[0])
          : snapshotEventValue(args);

    const entry: EventLogEntry = {
      seq: ++this.sequence,
      event,
      timestampMs,
      isoTime: new Date(timestampMs).toISOString(),
      listenerCount,
      ...(payload !== undefined ? { payload } : {}),
    };

    this.history.push(entry);
    if (this.history.length > this.historyLimit) {
      this.history.splice(0, this.history.length - this.historyLimit);
    }
  }

  /** Subscribe to an event. Returns an unsubscribe function. */
  on<K extends string>(
    event: K,
    callback: TypedCallback<Payload<K>>,
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as AnyCallback);
    return () => this.off(event, callback);
  }

  /** Subscribe to an event, but only fire once then auto-remove. */
  once<K extends string>(
    event: K,
    callback: TypedCallback<Payload<K>>,
  ): () => void {
    const wrapper = ((...args: any[]) => {
      this.off(event, wrapper);
      (callback as AnyCallback)(...args);
    }) as TypedCallback<Payload<K>>;
    return this.on(event, wrapper);
  }

  /** Unsubscribe a specific callback from an event. */
  off<K extends string>(
    event: K,
    callback: TypedCallback<Payload<K>>,
  ): void {
    const set = this.listeners.get(event);
    if (set) {
      set.delete(callback as AnyCallback);
      if (set.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /** Emit an event with its payload. */
  emit<K extends string>(
    event: K,
    ...args: EmitArgs<Payload<K>>
  ): void {
    const set = this.listeners.get(event);
    this.recordEvent(event, args as unknown[], set?.size ?? 0);
    if (set) {
      for (const cb of set) {
        cb(...(args as unknown[]));
      }
    }
  }

  /** Remove all listeners for a specific event, or all events if none specified. */
  clear(event?: string): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /** Number of listeners currently subscribed to an event. */
  listenerCount(event: string): number {
    return this.listeners.get(event)?.size ?? 0;
  }

  /** Return a snapshot of the most recent emitted events. */
  getHistory(limit = this.history.length): EventLogEntry[] {
    const normalized = Math.max(0, Math.floor(limit));
    if (normalized === 0) {
      return [];
    }
    return this.history.slice(-normalized).map((entry) => ({ ...entry }));
  }

  /** Drop all retained event-history entries without affecting listeners. */
  clearHistory(): void {
    this.history = [];
  }

  /** Configure the size of the retained event-history ring buffer. */
  setHistoryLimit(limit: number): void {
    this.historyLimit = normalizeHistoryLimit(limit);
    if (this.history.length > this.historyLimit) {
      this.history.splice(0, this.history.length - this.historyLimit);
    }
  }

  /** Current retained event-history limit. */
  getHistoryLimit(): number {
    return this.historyLimit;
  }
}

/** Singleton event bus for the game. */
export const gameEvents = new EventBus();
